import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import LeaderBoard from "../leaderboard/LeaderBoard";
import ActivitiesBoard from "../leaderboard/ActivitiesBoard";
import CommunityAvatar from "../community/CommunityAvatar";
import FlagIcon from "../icons/Flag";
import { getCommunities, getCommunityToDisplay } from "../../core/communities";
import { loadCommunitiesFromCache, saveCommunitiesToCache } from "../../core/communities/store";
import { useCurrentCommunity } from "../../utils/CurrentCommunityProvider";
import { useParams } from "react-router-dom";
import ChevronDownIcon from "../icons/ChevronDown";
import ChevronUpIcon from "../icons/ChevronUp";

const AsideCommunities = ({ currentPage = '', isFixed = false, context = 'header', shows = "nothing", setCurrentCommunity = () => { }, handleStartTour = () => { }, handleClickRequestCommunity = () => { }, hideMobileMenu = () => { }, selectedCommunityId = null, loggedInUser = null, communityBudges = {}, onForumOpen = () => { } }) => {
    const [communities, setCommunities] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [firstId, setFirstId] = useState(null);
    const navigate = useNavigate();
    const { comid } = useParams();
    const [openedCommunityIds, setOpenedCommunityIds] = useState([]);

    // Preserve x-position when switching to fixed
    const wrapperRef = useRef(null);

    const fetchCommunities = async () => {
        setIsLoading(true);

        // 1) Load from cache immediately
        const cached = loadCommunitiesFromCache();
        if (cached && Array.isArray(cached) && cached.length > 0) {
            setCommunities(cached);
            if (currentPage === 'communities') {
                setFirstId(cached[0]?._id || null);
            }
        }

        // 2) Fetch from server to refresh
        try {
            const data = await getCommunities();
            const unsortedCommunitiesData = data?.communities ?? [];
            if (unsortedCommunitiesData.length > 0) {
                if (currentPage === 'communities') {
                    setFirstId(unsortedCommunitiesData[0]._id);
                }
                setCommunities(unsortedCommunitiesData);
                saveCommunitiesToCache(unsortedCommunitiesData);
            }
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchCommunities();
    }, []);

    // Measure left/width when becoming fixed to preserve position
    // useEffect(() => {
    //     if (isFixed && wrapperRef.current) {
    //         const rect = wrapperRef.current.getBoundingClientRect();
    //         // viewport-based left works for position: fixed
    //         // setFixedLeftPx(`${Math.round(rect.left)}px`);
    //         // setFixedWidthPx(`${Math.round(rect.width)}px`);

    //         console.log("rect", `${Math.round(rect.left)}px`);

    //         wrapperRef.current.style.position = 'fixed';
    //         wrapperRef.current.style.left = `${Math.round(rect.left)}px`;
    //         wrapperRef.current.style.top = `${Math.round(rect.top)}px`;
    //         wrapperRef.current.style.width = `${Math.round(rect.width)}px`;
    //     } else if (!isFixed) {
    //         setFixedLeftPx(null);
    //         setFixedWidthPx(null);
    //     }
    // }, [isFixed]);

    useEffect(() => {
        if (isFixed.isFixed && wrapperRef.current) {
            // Measure in the next frame BEFORE any layout changes
            requestAnimationFrame(() => {
                const rect = wrapperRef.current.getBoundingClientRect();
                const centerPoint = window.innerWidth / 2;
                const totalGaps = 8;
                let left = centerPoint - (isFixed.mainWidth / 2) - rect.width - totalGaps;
                if (window.innerWidth < 1024) {
                    left = 8;
                }
                wrapperRef.current.style.position = 'fixed';
                wrapperRef.current.style.left = `${Math.round(left)}px`;
                wrapperRef.current.style.top = `${Math.round(rect.top)}px`;
            });
        } else {
            wrapperRef.current.style.position = 'relative';
            wrapperRef.current.style.left = 'auto';
            wrapperRef.current.style.top = 'auto';
        }
    }, [isFixed]);

    const fetchFirstCommunity = async (id) => {
        const community = await getCommunityToDisplay(id, 1);
        setCurrentCommunity(community);
    }

    // Register the first (top-most) community as current when communities load
    useEffect(() => {
        if (firstId) {
            // setCurrentCommunityId((prev) => {  
            //     return firstId; 
            // });
            setCurrentCommunity(null);
            fetchFirstCommunity(firstId);
            setOpenedCommunityIds([firstId]);

        }
    }, [firstId]);

    useEffect(() => {
        if (comid) {
            setOpenedCommunityIds([comid]);
        }
    }, [comid]);

    const handleClickCommunity = (community) => {
        hideMobileMenu();
        if (comid !== community._id || currentPage !== 'community') {
            setCurrentCommunity(null);
            navigate(`/community/${community._id}`);
        }
    }

    const handleToggleCommunity = (communityId) => {
        //if the community is already opened, close it, otherwise open it
        if (openedCommunityIds.includes(communityId)) {
            setOpenedCommunityIds(openedCommunityIds.filter((id) => id !== communityId));
        } else {
            setOpenedCommunityIds([...openedCommunityIds, communityId]);
        }
    }

    // Roll up forum-level "new posts" counts so collapsed community rows still
    // hint where the unread content lives. Mirrors the sum that drives the
    // single Communities pill in the nav header.
    const communityBudgeCounts = useMemo(() => {
        const counts = {};
        for (const c of communities) {
            let n = 0;
            const forums = Array.isArray(c?.forums) ? c.forums : [];
            for (const f of forums) {
                const arr = communityBudges?.[f?._id];
                if (Array.isArray(arr)) {
                    n += arr.length;
                }
            }
            counts[c._id] = n;
        }
        return counts;
    }, [communities, communityBudges]);

    const getForumBudgeCount = (forumId) => {
        const arr = communityBudges?.[forumId];
        return Array.isArray(arr) ? arr.length : 0;
    };

    const handleClickForum = (community, forum) => {
        hideMobileMenu();
    };


    // stylex={isFixed ? { left: fixedLeftPx || undefined, width: fixedWidthPx || undefined } : undefined}
    // className={`aside-wrapper ${isFixed ? "fixed" : ""} `}
    return (
        <div
            ref={wrapperRef}
            className="aside-wrapper"
            id="aside-communities-wrapper"
        >
            <aside className={`mb-2 rounded-md  ${context == 'header' ? 'flex' : 'hidden'}  md:flex flex-col gap-y-1  md:gap-y-2 top-14 w-full md:w-80 shrink-0 p-0`}>

                <div className={`flex flex-col h-fit w-full bg-slate-100 rounded-md ${context == 'header' ? '' : ''}`}>

                    {/* <button
            class="flex flex-row justify-between items-center px-2 text-center text-white font-bold py-1 border-b rounded-md border-gray-200 md:border-0 bg-purple-900 hover:bg-purple-800  cursor-pointer mb-1 m-1"
            onClick={handleClickRequestCommunity}
        >
            Request a Community
            <CommunityRequestIcon className={"text-white"} />
        </button> */}

                    {context != 'header' &&
                        <button onClick={handleStartTour} className="flex flex-row justify-between items-center px-2 text-center text-white font-bold py-1 border-b rounded-md border-gray-200 md:border-0 bg-purple-900 hover:bg-purple-800  cursor-pointer mb-1 m-1">
                            Get Started
                            <FlagIcon className={"text-white"} />
                        </button>
                    }



                    <div id="aside-communities-list" className="flex flex-row justify-center items-center px-2 text-center font-bold py-2 border-b border-gray-200  bg-primary-white">
                        Communities
                    </div>


                    <div className="flex flex-col  rounded-b-md pb-1  ">
                        <div

                            className={
                                `p-2 pl-5 even:bg-slate-100 odd:bg-white cursor-pointer flex flex-row justify-start items-center  text-purple-900`
                            }

                            onClick={handleClickRequestCommunity}

                        >

                            <div className="min-w-[30px] h-[30px] flex justify-center items-center  ">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-6 w-6">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                </svg>
                            </div>

                            <div className="flex flex-col justify-start items-start  ">
                                <div className="text-sm font-bold ">
                                    Request a Community
                                </div>
                            </div>
                        </div>

                        {/* filter(community => (loggedInUser?.role === "admin") ? true : (community?.forums?.length > 0)) */}

                        {communities.map((community, index) => {
                            const communityBudgeCount = communityBudgeCounts[community._id] ?? 0;
                            return <div key={community._id} className={"flex flex-col w-full  items-center even:bg-slate-100 odd:bg-white cursor-pointer " + (firstId === community._id || comid === community._id ? '!bg-purple-100 ' : '') +
                                'hover:bg-purple-50'}>
                                <div className="flex flex-row justify-start items-center w-full">
                                    <div className="flex flex-row justify-center items-center pl-2" onClick={(e) => { e.preventDefault(); handleToggleCommunity(community._id) }}>
                                        {/* chevron down icon */}
                                        {openedCommunityIds.includes(community._id) == false && <ChevronDownIcon className="text-purple-900 w-7 h-7" />}
                                        {openedCommunityIds.includes(community._id) && <ChevronUpIcon className="text-purple-900 w-7 h-7" />}
                                    </div>
                                    <div
                                        className={
                                            `p-2  flex flex-row justify-start items-center flex-1 `

                                        }
                                        onClick={() => {
                                            // Auto-expand on click when the community has unread
                                            // posts so the user is one step closer to the forum
                                            // that caused the nav-menu badge.
                                            if (communityBudgeCount > 0 && !openedCommunityIds.includes(community._id)) {
                                                setOpenedCommunityIds((prev) => prev.includes(community._id) ? prev : [...prev, community._id]);
                                            }
                                            handleClickCommunity(community);
                                        }}
                                    >

                                        <div className="min-w-[40px] h-[40px] bg-purple-900x flex justify-center items-center text-purple-300 mr-2 ">
                                            <CommunityAvatar community={community} isRound={true} />
                                        </div>

                                        <div className="flex flex-col justify-start items-start  ">
                                            <div className="text-sm  ">
                                                {community.title}
                                            </div>
                                            {/* <div className="text-xxs text-gray-500">
                                        {getCommunityStatistics(community)}
                                    </div> */}
                                        </div>
                                    </div>
                                    {communityBudgeCount > 0 && (
                                        <span
                                            className="mr-3 ml-1 bg-red-600 text-white rounded-full text-[10px] leading-none px-1.5 py-0.5 min-w-[18px] text-center font-semibold"
                                            aria-label={`${communityBudgeCount} new post${communityBudgeCount === 1 ? '' : 's'}`}
                                        >
                                            {communityBudgeCount}
                                        </span>
                                    )}
                                </div>
                                <div className="w-full">
                                    {openedCommunityIds.includes(community._id) == true && (
                                        <div className="flex flex-col w-full">
                                            {community?.forums?.map((forum) => {
                                                const forumBudgeCount = getForumBudgeCount(forum._id);
                                                return (
                                                    <a
                                                        href={`/community/${community._id}/forum/${forum._id}`}
                                                        key={forum._id}
                                                        onClick={() => handleClickForum(community, forum)}
                                                        className="w-full pl-10 text-sm text-gray-700 border-b border-gray-200 px-2 py-3 hover:bg-purple-100 cursor-pointer flex flex-row items-center justify-between gap-x-2"
                                                    >
                                                        <span className="truncate">{forum.title}</span>
                                                        {forumBudgeCount > 0 && (
                                                            <span
                                                                className="bg-red-600 text-white rounded-full text-[10px] leading-none px-1.5 py-0.5 min-w-[18px] text-center font-semibold shrink-0"
                                                                aria-label={`${forumBudgeCount} new post${forumBudgeCount === 1 ? '' : 's'}`}
                                                            >
                                                                {forumBudgeCount}
                                                            </span>
                                                        )}
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        })}
                    </div>
                </div>


                {/* <LeaderBoard handleStartTour={handleStartTour} context={context}  /> */}


                {context === "" &&
                    <ActivitiesBoard context={context} />
                }

            </aside>
        </div>
    );
};



export default AsideCommunities;